import { useRef, useState } from 'react';
import { useChestStore } from '../../store/chestSlice';
import { useInventoryStore } from '../../store/inventorySlice';
import { closeInterface } from '../../services/InterfaceService';
import { INVENTORY_COLS, ITEMS } from '../../data/inventory.config';
import { UI_FORGE_GRID_FRAME_REGION } from '../../data/ui.atlas';
import { describeItemCard } from '../../services/CardDescriptionService';
import { ItemList, ItemListEntry } from './ItemList';
import { NineSliceFrame } from './NineSliceFrame';
import { KeyDisplay } from './KeyDisplay';
import './ChestScreen.scss';

type Side = 'inv' | 'chest';

// Visible rows per side; both lists window past this (offset, no scrollbar) so
// the chest's unlimited capacity stays fully navigable. Gold is the last entry.
const VISIBLE_ROWS = 4;

/**
 * The chest has virtually unlimited capacity; for the inventory, any transfer
 * that does not fit (`addItem` overflow) stays on the chest side.
 */
export const ChestScreen = () => {
  const [side, setSide] = useState<Side>('inv');

  const invStore = useInventoryStore();
  const chestStore = useChestStore();
  const stateRef = useRef({ invStore, chestStore });
  stateRef.current = { invStore, chestStore };

  type Focus =
    | { side: Side; kind: 'item'; itemId: string; quantity: number }
    | { side: Side; kind: 'gold'; quantity: number };

  const resolveFocus = (id: string): Focus | null => {
    const [prefix, rawIdx] = id.split(':');
    if (prefix !== 'inv' && prefix !== 'chest') {
      return null;
    }
    const focusSide: Side = prefix;
    const store =
      focusSide === 'inv'
        ? stateRef.current.invStore
        : stateRef.current.chestStore;
    if (rawIdx === 'gold') {
      return { side: focusSide, kind: 'gold', quantity: store.gold };
    }
    const idx = parseInt(rawIdx, 10);
    const slot = Number.isNaN(idx) ? null : store.slots[idx];
    if (!slot) {
      return null;
    }
    return {
      side: focusSide,
      kind: 'item',
      itemId: slot.itemId,
      quantity: slot.quantity,
    };
  };

  const transferAmount = (navId: string, amount: number) => {
    const focus = resolveFocus(navId);
    if (!focus || amount <= 0) {
      return;
    }
    const { invStore: inv, chestStore: chest } = stateRef.current;
    const moving = Math.min(amount, focus.quantity);
    if (moving <= 0) {
      return;
    }
    if (focus.kind === 'gold') {
      if (focus.side === 'inv') {
        if (inv.spendGold(moving)) {
          chest.addGold(moving);
        }
      } else if (chest.removeGold(moving)) {
        inv.addGold(moving);
      }
      return;
    }
    if (focus.side === 'inv') {
      const removed = inv.removeItem(focus.itemId, moving);
      if (removed > 0) {
        chest.addItem(focus.itemId, removed);
      }
    } else {
      const leftover = inv.addItem(focus.itemId, moving);
      const moved = moving - leftover;
      if (moved > 0) {
        chest.removeItem(focus.itemId, moved);
      }
    }
  };

  const dumpInventoryToChest = () => {
    const { invStore: inv, chestStore: chest } = stateRef.current;
    for (const slot of inv.slots) {
      if (slot) {
        chest.addItem(slot.itemId, slot.quantity);
      }
    }
    if (inv.gold > 0) {
      chest.addGold(inv.gold);
      inv.spendGold(inv.gold);
    }
    inv.clearInventory();
  };

  const sortBoth = () => {
    stateRef.current.invStore.sortByType();
    stateRef.current.chestStore.sortByType();
  };

  const switchSide = () => setSide((s) => (s === 'inv' ? 'chest' : 'inv'));

  const buildItems = (
    s: Side,
    slots: typeof invStore.slots,
    count: number,
  ): (ItemListEntry | null)[] => {
    const cells: (ItemListEntry | null)[] = [];
    for (let i = 0; i < count; i++) {
      const slot = slots[i];
      const def = slot ? ITEMS[slot.itemId] : null;
      cells.push(
        slot && def
          ? {
              id: `${s}:${i}`,
              navId: `${s}:${i}`,
              icon: def.sprite,
              overlay: def.iconOverlay,
              quantity: slot.quantity,
              card: {
                icon: def.sprite,
                overlay: def.iconOverlay,
                name: def.label,
                subtitle:
                  def.description ?? `${slot.quantity} / ${def.maxStack}`,
                ...(describeItemCard(slot.itemId) ?? {}),
              },
            }
          : null,
      );
    }
    return cells;
  };

  // Gold lives in its own counter, not a grid slot — rendered apart, left of
  // the first cell (cf. ItemList `goldEntry`).
  const buildGold = (s: Side, gold: number): ItemListEntry => ({
    id: `${s}:gold`,
    navId: `${s}:gold`,
    icon: ITEMS.gold.sprite,
    quantity: gold,
    card: {
      icon: ITEMS.gold.sprite,
      name: ITEMS.gold.label,
      subtitle: `${gold}`,
    },
  });

  const invItems = buildItems('inv', invStore.slots, invStore.slots.length);
  const chestItems = buildItems(
    'chest',
    chestStore.slots,
    chestStore.slots.length,
  );

  const sharedHandlers = {
    onConfirm: (item: ItemListEntry) => transferAmount(item.navId, 1),
    onSecondary: (item: ItemListEntry) => {
      const focus = resolveFocus(item.navId);
      if (focus) {
        transferAmount(item.navId, focus.quantity);
      }
    },
    onTertiary: sortBoth,
    onQuaternary: dumpInventoryToChest,
    onTabNext: switchSide,
    onTabPrev: switchSide,
    onCancel: () => closeInterface(),
    onPause: () => closeInterface(),
  };

  return (
    <div className="chest-overlay">
      <div className="chest-screen">
        <div className="chest-screen-header">
          <h1>Coffre du rêve</h1>
        </div>

        <div className="chest-screen-grids">
          <section
            className={`chest-screen-side${side === 'inv' ? ' is-active' : ''}`}
          >
            <h3 className="chest-screen-side-title">Inventaire</h3>
            <NineSliceFrame
              region={UI_FORGE_GRID_FRAME_REGION}
              className="chest-grid-frame"
            >
              <ItemList
                active={side === 'inv'}
                items={invItems}
                goldEntry={buildGold('inv', invStore.gold)}
                cols={INVENTORY_COLS}
                maxRows={VISIBLE_ROWS}
                {...sharedHandlers}
              />
            </NineSliceFrame>
          </section>
          <section
            className={`chest-screen-side${side === 'chest' ? ' is-active' : ''}`}
          >
            <h3 className="chest-screen-side-title">Coffre</h3>
            <NineSliceFrame
              region={UI_FORGE_GRID_FRAME_REGION}
              className="chest-grid-frame"
            >
              <ItemList
                active={side === 'chest'}
                items={chestItems}
                goldEntry={buildGold('chest', chestStore.gold)}
                cols={INVENTORY_COLS}
                maxRows={VISIBLE_ROWS}
                {...sharedHandlers}
              />
            </NineSliceFrame>
          </section>
        </div>

        <div className="chest-screen-hint">
          <KeyDisplay context="interface" action="CONFIRM" size="sm" /> Déplacer
          une unité ·{' '}
          <KeyDisplay context="interface" action="SECONDARY" size="sm" />{' '}
          Déplacer toute la pile ·{' '}
          <KeyDisplay context="interface" action="QUATERNARY" size="sm" /> Vider
          l&apos;inventaire ·{' '}
          <KeyDisplay context="interface" action="TERTIARY" size="sm" /> Trier ·{' '}
          <KeyDisplay context="interface" action="DETAILS" size="sm" /> Détails
          ·{' '}
          <span className="chest-screen-hint-tabs">
            <KeyDisplay context="interface" action="TAB_PREV" size="sm" />
            <KeyDisplay context="interface" action="TAB_NEXT" size="sm" />
          </span>{' '}
          Changer de côté ·{' '}
          <KeyDisplay context="interface" action="CANCEL" size="sm" /> Retour
        </div>
      </div>
    </div>
  );
};
